import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import {
  CALENDAR_DATE_SOURCE,
  CRM_OCCASION_SOURCE,
  RUN_ALERT_STATUSES,
  RUN_STATUS_RUNNING,
  findDateSource,
  formatCalendarDate,
  offsetModeFor,
  runErrorCode,
} from './dateTrigger';

const formatHour = hour => `${String(hour).padStart(2, '0')}:00`;
const padDay = value => String(value).padStart(2, '0');

// Resumo de um gatilho por data e da última execução, usado na lista de automações e na aba
// Campanhas do CRM.
export function useDateTriggerSummary() {
  const { t, locale } = useI18n();
  const customAttributes = useMapGetter('attributes/getAttributes');

  const sourceLabel = dateTrigger => {
    const {
      source_type: sourceType,
      attribute_key: attributeKey,
      occasion_type: occasionType,
      commemorative_key: commemorativeKey,
      calendar_month: calendarMonth,
      calendar_day: calendarDay,
    } = dateTrigger;
    if (sourceType === CALENDAR_DATE_SOURCE) {
      return commemorativeKey
        ? t(`CRM_RECOMPRA.DATES.${commemorativeKey.toUpperCase()}`)
        : `${padDay(calendarDay)}/${padDay(calendarMonth)}`;
    }
    if (sourceType === CRM_OCCASION_SOURCE) {
      return t('AUTOMATION.DATE_TRIGGER.CRM.OCCASION_FIELD', {
        type: t(
          `AUTOMATION.DATE_TRIGGER.CRM.OCCASION_TYPES.${occasionType.toUpperCase()}`
        ),
      });
    }
    const attributeModel = findDateSource(sourceType)?.attributeModel;
    if (!attributeModel) {
      return t(
        `AUTOMATION.DATE_TRIGGER.SOURCE.OPTIONS.${sourceType.toUpperCase()}`
      );
    }
    const attribute = (customAttributes.value || []).find(
      item =>
        item.attribute_model === attributeModel &&
        item.attribute_key === attributeKey
    );
    return (
      attribute?.attribute_display_name ||
      t('AUTOMATION.DATE_TRIGGER.SUMMARY.FIELD_DELETED')
    );
  };

  const dateSummary = dateTrigger => {
    const field = sourceLabel(dateTrigger);
    const time = formatHour(dateTrigger.run_hour);
    // Datas do cliente sem deslocamento usam a antecedência cadastrada em cada data.
    if (dateTrigger.offset_days === null) {
      return t('AUTOMATION.DATE_TRIGGER.SUMMARY.LEAD_DAYS', { field, time });
    }
    const count = Math.abs(dateTrigger.offset_days);
    return t(
      `AUTOMATION.DATE_TRIGGER.SUMMARY.${offsetModeFor(dateTrigger.offset_days).toUpperCase()}`,
      { count, field, time },
      count
    );
  };

  // Runs que não executaram nada viram alerta em vez da contagem.
  const lastRunAlert = run => {
    if (!run || !RUN_ALERT_STATUSES.includes(run.status)) return null;
    return t(
      `AUTOMATION.DATE_TRIGGER.LAST_RUN.ERRORS.${runErrorCode(run).toUpperCase()}`
    );
  };

  const lastRunText = run => {
    if (!run || lastRunAlert(run)) return null;
    const date = formatCalendarDate(run.run_on, locale.value, {
      withYear: false,
    });
    if (run.status === RUN_STATUS_RUNNING) {
      return t('AUTOMATION.DATE_TRIGGER.LAST_RUN.RUNNING', { date });
    }
    return t(
      'AUTOMATION.DATE_TRIGGER.LAST_RUN.EXECUTED',
      { date, count: run.executed_count },
      run.executed_count
    );
  };

  return { dateSummary, lastRunAlert, lastRunText };
}
